/**
 * Deterministic pseudo-randomness.
 *
 * Every bit of physical variation in the scrapbook — how far a print is
 * rotated, which mount style it gets, where the tape sits, how the date stamp
 * is angled — is derived from a hash of the entry's UUID. So a find looks
 * identical on every render and every reload, but the feed never looks
 * mechanical.
 *
 * Rotations stay tiny on purpose. This should read as care, not chaos.
 */

/** FNV-1a, 32-bit. Cheap, stable, and well spread for short strings. */
export function hashSeed(id: string, salt = ""): number {
  const input = salt ? `${id}::${salt}` : id;
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    // hash *= 16777619, in 32-bit safe pieces
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

/** A stable float in [0, 1) derived from a seed. */
export function seededUnit(seed: number): number {
  // One extra round of mixing so adjacent seeds don't produce adjacent floats.
  let x = seed >>> 0;
  x ^= x >>> 16;
  x = Math.imul(x, 0x7feb352d) >>> 0;
  x ^= x >>> 15;
  x = Math.imul(x, 0x846ca68b) >>> 0;
  x ^= x >>> 16;
  return (x >>> 0) / 0x100000000;
}

/** Pick a stable element from a non-empty list. */
export function seededPick<T>(seed: number, array: readonly T[]): T {
  if (array.length === 0) {
    throw new Error("seededPick needs a non-empty array");
  }
  const index = Math.floor(seededUnit(seed) * array.length) % array.length;
  return array[index] as T;
}

/** A stable float within [min, max), rounded to `precision` decimals. */
export function seededRange(seed: number, min: number, max: number, precision = 2): number {
  const value = min + seededUnit(seed) * (max - min);
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
}

/** A stable integer within [min, max], inclusive. */
export function seededInt(seed: number, min: number, max: number): number {
  return min + Math.floor(seededUnit(seed) * (max - min + 1));
}

/** A stable coin flip, biased by `chance` (0..1). */
export function seededChance(seed: number, chance: number): boolean {
  return seededUnit(seed) < chance;
}

/* -------------------------------------------------------------------------- */
/* The scrapbook's physical vocabulary                                        */
/* -------------------------------------------------------------------------- */

/** How the print is held to the sheet. */
export const MOUNT_STYLES = ["corners", "tape", "paste"] as const;
export type MountStyle = (typeof MOUNT_STYLES)[number];

/** Where a strip of tape lands, when there is tape. */
export const TAPE_PLACEMENTS = ["top", "top-left", "top-right", "corners"] as const;
export type TapePlacement = (typeof TAPE_PLACEMENTS)[number];

export type EntryVariation = {
  /** Degrees, ±1.5. */
  rotation: number;
  mount: MountStyle;
  tape: TapePlacement;
  /** Degrees, for the rubber-stamped date. */
  stampRotation: number;
  /** Which accent the date stamp is inked in. */
  stampInk: "ochre" | "teal";
  /** Sub-pixel nudges, so nothing sits perfectly square. */
  offsetX: number;
  offsetY: number;
  /** Plate number for field-guide captions, stable per entry. */
  plateNumber: number;
};

const varyCache = new Map<string, EntryVariation>();

/**
 * The single source of physical variation for an entry. Memoised, since it is
 * called during render by several components for the same id.
 */
export function variationFor(id: string): EntryVariation {
  const cached = varyCache.get(id);
  if (cached) return cached;

  const variation: EntryVariation = {
    rotation: seededRange(hashSeed(id, "rotation"), -1.5, 1.5, 2),
    mount: seededPick(hashSeed(id, "mount"), MOUNT_STYLES),
    tape: seededPick(hashSeed(id, "tape"), TAPE_PLACEMENTS),
    stampRotation: seededRange(hashSeed(id, "stamp"), -4.5, 4.5, 1),
    stampInk: seededChance(hashSeed(id, "ink"), 0.62) ? "ochre" : "teal",
    offsetX: seededRange(hashSeed(id, "dx"), -3, 3, 1),
    offsetY: seededRange(hashSeed(id, "dy"), -2, 2, 1),
    plateNumber: seededInt(hashSeed(id, "plate"), 1, 312),
  };

  varyCache.set(id, variation);
  return variation;
}
